import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import Button from '@/components/common/Button';
import recruiterCandidateService, { RevealedCandidate } from '@/services/recruiterCandidateService';

import SentOffersTab from '@/components/recruiter/tabs/SentOffersTab';

export default function JobOffersManager() {
  const { t } = useTranslation('jobOffers');
  const navigate = useNavigate();
  const [showCandidateSearch, setShowCandidateSearch] = useState(false);

  // Candidate search state
  const [candidates, setCandidates] = useState<RevealedCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidateSearchQuery, setCandidateSearchQuery] = useState('');
  const [candidatePagination, setCandidatePagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchCandidates = async () => {
    try {
      setCandidatesLoading(true);
      const response = await recruiterCandidateService.searchRevealedCandidates({
        search: candidateSearchQuery || undefined,
        page: candidatePagination.page,
        limit: candidatePagination.limit,
      });

      setCandidates(response.candidates);
      setCandidatePagination(response.pagination);
    } catch (error) {
      console.error('Failed to fetch candidates:', error);
    } finally {
      setCandidatesLoading(false);
    }
  };

  const handleSendOfferClick = () => {
    setShowCandidateSearch(true);
    setCandidateSearchQuery('');
    setCandidatePagination({ page: 1, limit: 10, total: 0, totalPages: 0 });
    fetchCandidates();
  };

  const handleCandidateSelect = (candidate: RevealedCandidate) => {
    const name = `${candidate.firstName} ${candidate.lastName}`;
    setShowCandidateSearch(false);
    navigate(`/recruiter/job-offers/new?candidateId=${candidate.id}&candidateName=${encodeURIComponent(name)}`);
  };

  return (
    <div className="container-custom py-8">
      {/* Header with Send Offer Button */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('manager.title')}
          </h1>
          <p className="text-gray-600">{t('manager.subtitle')}</p>
        </div>

        <Button
          variant="primary"
          onClick={handleSendOfferClick}
          className="flex items-center gap-2 whitespace-nowrap"
        >
          <Plus className="w-5 h-5" />
          {t('manager.sendOfferButton')}
        </Button>
      </div>

      <SentOffersTab />

      {/* Candidate Search Modal */}
      {showCandidateSearch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="absolute inset-0 backdrop-blur-sm bg-black/50" onClick={() => setShowCandidateSearch(false)} />
          <div className="relative w-full max-w-2xl bg-white/70 backdrop-blur-sm rounded-2xl shadow-2xl p-6 animate-scale-in border border-gray-200 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{t('offers.selectCandidate.title')}</h2>
              <button
                onClick={() => setShowCandidateSearch(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors p-2 rounded-lg hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Search Input */}
            <div className="mb-6">
              <input
                type="text"
                placeholder={t('offers.selectCandidate.searchPlaceholder')}
                value={candidateSearchQuery}
                onChange={(e) => setCandidateSearchQuery(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setCandidatePagination({ ...candidatePagination, page: 1 });
                    fetchCandidates();
                  }
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder:text-gray-400"
              />
            </div>

            {/* Candidates List */}
            {candidatesLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : candidates.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">{t('offers.selectCandidate.noCandidates')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {candidates.map((candidate) => (
                  <button
                    key={candidate.id}
                    onClick={() => handleCandidateSelect(candidate)}
                    className="w-full text-left p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md hover:border-blue-300 transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {candidate.firstName} {candidate.lastName}
                        </h3>
                        {candidate.email && (
                          <p className="text-sm text-gray-600">{candidate.email}</p>
                        )}
                        {candidate.jobTitle && (
                          <p className="text-sm text-gray-700 mt-1">{candidate.jobTitle}</p>
                        )}
                        {candidate.location && (
                          <p className="text-xs text-gray-500 mt-1">{candidate.location}</p>
                        )}
                      </div>
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-300">
                        {t('offers.selectCandidate.revealed')}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Pagination */}
            {candidatePagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setCandidatePagination({ ...candidatePagination, page: candidatePagination.page - 1 });
                    fetchCandidates();
                  }}
                  disabled={candidatePagination.page === 1}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common:button.previous')}
                </button>
                <span className="text-sm text-gray-600">
                  {t('common:pagination.page', { current: candidatePagination.page, total: candidatePagination.totalPages })}
                </span>
                <button
                  onClick={() => {
                    setCandidatePagination({ ...candidatePagination, page: candidatePagination.page + 1 });
                    fetchCandidates();
                  }}
                  disabled={candidatePagination.page >= candidatePagination.totalPages}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common:button.next')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
